import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * GT-07: korisničko sučelje na srpskom jeziku — latinica ili ćirilica.
 * Tekstovi se pišu latinicom, ćirilica se dobija determinističkom transliteracijom.
 *
 * Šta ostaje latinicom:
 *  1. izrazi iz liste „Izrazi bez transliteracije“ (Administracija → Šifarnici i šabloni) — tehnički termini,
 *     nazivi proizvoda, formati, jedinice; listu uređuje administrator, učitava se sa /api/v1/script-terms;
 *  2. automatski: strane riječi (sa q, w, x, y), internet i e-adrese, putanje, nazivi fajlova, korisnička imena/ključevi sa tačkom
 *     (ime.prezime, rugipp.url) i oznake koje počinju slovom a sadrže cifru (AS-2026-00905, EPSG:8678, B08).
 *
 * Pored `t()` u komponentama, kada je izabrana ćirilica, `CyrillicDom` prevodi i tekst koji nije prošao kroz `t()`
 * (podaci iz baze, poruke servera, uputstvo, iskačući prozori). Ne dira polja za unos, kod i elemente sa
 * atributom `data-no-translit` ili `translate="no"`.
 */
const DIGRAPHS: [string, string][] = [
  ["Lj", "Љ"], ["LJ", "Љ"], ["lj", "љ"], ["Nj", "Њ"], ["NJ", "Њ"], ["nj", "њ"], ["Dž", "Џ"], ["DŽ", "Џ"], ["dž", "џ"],
];
const SINGLE: Record<string, string> = {
  A: "А", B: "Б", V: "В", G: "Г", D: "Д", Đ: "Ђ", E: "Е", Ž: "Ж", Z: "З", I: "И", J: "Ј", K: "К", L: "Л", M: "М",
  N: "Н", O: "О", P: "П", R: "Р", S: "С", T: "Т", Ć: "Ћ", U: "У", F: "Ф", H: "Х", C: "Ц", Č: "Ч", Š: "Ш",
  a: "а", b: "б", v: "в", g: "г", d: "д", đ: "ђ", e: "е", ž: "ж", z: "з", i: "и", j: "ј", k: "к", l: "л", m: "м",
  n: "н", o: "о", p: "п", r: "р", s: "с", t: "т", ć: "ћ", u: "у", f: "ф", h: "х", c: "ц", č: "ч", š: "ш",
};

/** Rezervna lista dok se ne učita lista iz baze (ili ako server nije dostupan). */
const FALLBACK_TERMS = (
  "AgriSense NDVI NDRE GNDVI CIRE CIre EVI MSAVI2 SAVI BSI NDMI RI SCI NSMI UAV LAZ LAS COG COPC GeoTIFF DSM DTM nDSM WMS " +
  "WFS WFS-T WMTS WCS WPS CSW SLD GeoJSON SHP KML KMZ GPKG PDF CSV XLSX API Swagger Keycloak GeoServer GeoFence MFA OTP " +
  "Sentinel-2 Sentinel Copernicus CDSE EPSG ID BBCH GDD HTML SHA-256 R² MAE OGC URL MVT XYZ OSM Esri " +
  "JSON WKT SMTP OAuth ZIP PNG GIS ha m2 kg"
).split(" ").map((term) => ({ term, case_sensitive: true }));

export interface ScriptTerm { term: string; case_sensitive: boolean }
export type KeepReason = "izraz" | "adresa" | "e-pošta" | "putanja" | "fajl" | "ključ" | "oznaka" | "strana riječ" | "kod";

const B = "(?<![\\p{L}\\p{N}_])"; // početak riječi (radi i sa č, ć, š, ž, đ)
const E = "(?![\\p{L}\\p{N}_])";  // kraj riječi
const AUTO: [RegExp, KeepReason][] = [
  [/(?:https?|ftp):\/\/[^\s<>"'«»“”]+|www\.[^\s<>"'«»“”]+/gu, "adresa"],
  [/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/gu, "e-pošta"],
  [/(?<![\p{L}\p{N}])\/(?:[A-Za-z0-9_.{}:-]+\/?)+(?:\?[^\s]*)?/gu, "putanja"],
  [/[\w-]+\.(?:laz|las|tif|tiff|zip|shp|kml|kmz|geojson|gpkg|gml|pdf|csv|xlsx|xls|docx|png|jpe?g|json|xml|html|txt|command|sh|py|yml|yaml|env|conf)(?![\p{L}\p{N}])/giu, "fajl"],
  [new RegExp(`${B}[A-Za-z_][A-Za-z0-9_-]*(?:\\.[A-Za-z_][A-Za-z0-9_-]*)+${E}`, "gu"), "ključ"],
  [/\{\s*"[^{}]*\}/gu, "kod"], // JSON iz poruka spoljnih servisa
];
const HEX: [RegExp, KeepReason] = [new RegExp(`${B}[0-9a-f]{12,}${E}`, "gu"), "kod"]; // sha256, identifikatori
// riječ sa slovima kojih nema u srpskoj latinici (q, w, x, y) je strana — WeasyPrint, openpyxl, Swagger
const FOREIGN: [RegExp, KeepReason] = [new RegExp(`${B}[A-Za-z-]*[QWXYqwxy][A-Za-z-]*${E}`, "gu"), "strana riječ"];
// tehnički identifikatori sa donjom crtom ili dvotačkom (role, permisije, šifre indeksa): opstinski_referent, cases:read, BSI_MS
const IDENT: [RegExp, KeepReason] = [new RegExp(`${B}[A-Za-z][A-Za-z0-9]*(?:[_:][A-Za-z0-9]+)+${E}`, "gu"), "kod"];
const CODE: [RegExp, KeepReason] = [new RegExp(`${B}[A-Za-z][A-Za-z0-9_:-]*\\d[A-Za-z0-9_:-]*${E}`, "gu"), "oznaka"];

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
let termRules: [RegExp, KeepReason][] = [];
function compile(terms: ScriptTerm[]) {
  const sorted = [...terms].filter((t) => t.term.trim()).sort((a, b) => b.term.length - a.term.length);
  const cs = sorted.filter((t) => t.case_sensitive).map((t) => esc(t.term.trim()));
  const ci = sorted.filter((t) => !t.case_sensitive).map((t) => esc(t.term.trim()));
  termRules = [];
  if (cs.length) termRules.push([new RegExp(`${B}(?:${cs.join("|")})${E}`, "gu"), "izraz"]);
  if (ci.length) termRules.push([new RegExp(`${B}(?:${ci.join("|")})${E}`, "giu"), "izraz"]);
}
compile(FALLBACK_TERMS);

export interface Segment { text: string; kept?: KeepReason }

/** Dijeli tekst na dijelove koji ostaju latinicom (sa razlogom) i dijelove koji se transliterišu. */
export function segments(text: string, terms?: ScriptTerm[]): Segment[] {
  const saved = termRules;
  if (terms) compile(terms);
  const kept: Segment[] = [];
  let t = text;
  for (const [rx, why] of [...AUTO, ...termRules, HEX, IDENT, CODE, FOREIGN]) {
    t = t.replace(rx, (m) => {
      kept.push({ text: m, kept: why });
      return `\u0000${kept.length - 1}\u0000`;
    });
  }
  if (terms) termRules = saved;
  const out: Segment[] = [];
  for (const part of t.split(/(\u0000\d+\u0000)/)) {
    if (!part) continue;
    const m = /^\u0000(\d+)\u0000$/.exec(part);
    out.push(m ? kept[Number(m[1])] : { text: translit(part) });
  }
  return out;
}

function translit(s: string): string {
  let t = s;
  for (const [l, c] of DIGRAPHS) t = t.split(l).join(c);
  return t.replace(/[A-Za-zĐđŽžĆćČčŠš]/g, (ch) => SINGLE[ch] ?? ch);
}

const HAS_LATIN = /[A-Za-zĐđŽžĆćČčŠš]/;
export function toCyrillic(text: string): string {
  if (!text || !HAS_LATIN.test(text)) return text;
  return segments(text).map((s) => s.text).join("");
}

// ------------------------------------------------------------------ prevođenje teksta koji nije prošao kroz t()
const SKIP = "input, textarea, select, code, pre, kbd, samp, script, style, noscript, canvas, [contenteditable=''], " +
  "[contenteditable='true'], [data-no-translit], [translate='no']";
const ATTRS = ["placeholder", "title", "aria-label", "alt"];
const touched = new Map<Node, { orig: string; conv: string }>();   // tekstualni čvor → izvorni i preveden tekst
const touchedAttr = new Map<Element, Record<string, { orig: string; conv: string }>>();

// samo sadržaj aplikacije (root i Mantine portali) — ne dira elemente koje ubacuju dodaci pregledača
const OWN = "#root, [data-portal], .mantine-Notifications-root";
function skip(el: Element | null): boolean {
  return !el || !!el.closest(SKIP) || !el.closest(OWN);
}

function convertText(n: Text) {
  const v = n.nodeValue ?? "";
  const rec = touched.get(n);
  if (rec && rec.conv === v) return; // već prevedeno
  if (!HAS_LATIN.test(v) || skip(n.parentElement)) return;
  const c = toCyrillic(v);
  if (c !== v) {
    touched.set(n, { orig: v, conv: c });
    n.nodeValue = c;
  }
}

function convertAttrs(el: Element) {
  if (el.closest("[data-no-translit], [translate='no'], code, pre") || !el.closest(OWN)) return;
  for (const a of ATTRS) {
    const v = el.getAttribute(a);
    if (!v || !HAS_LATIN.test(v)) continue;
    const rec = touchedAttr.get(el) ?? {};
    if (rec[a]?.conv === v) continue;
    const c = toCyrillic(v);
    if (c !== v) {
      rec[a] = { orig: v, conv: c };
      touchedAttr.set(el, rec);
      el.setAttribute(a, c);
    }
  }
}

/** Prikazna vrijednost polja koje se ne kuca (Select bez pretrage, onemogućeno polje) — npr. izabrana opština. */
const touchedInput = new Map<HTMLInputElement, { orig: string; conv: string }>();
function convertInput(el: HTMLInputElement) {
  if (el.type === "hidden" || el.closest("[data-no-translit], [translate='no']") || !el.closest(OWN)) return;
  // Select sa pretragom prikazuje naziv izabrane stavke — prevodi se dok polje nije u fokusu (kucanje ostaje latinicom)
  const selectLabel = el.classList.contains("mantine-Select-input") && document.activeElement !== el;
  if (!(el.readOnly || el.disabled || selectLabel)) return;
  const v = el.value;
  const rec = touchedInput.get(el);
  if ((rec && rec.conv === v) || !HAS_LATIN.test(v)) return;
  const c = toCyrillic(v);
  if (c !== v) {
    touchedInput.set(el, { orig: v, conv: c });
    el.value = c;
  }
}

function convertTree(root: Node): void {
  if (root.nodeType === Node.ELEMENT_NODE && (root as Element).tagName === "INPUT") {
    convertAttrs(root as Element);
    return convertInput(root as HTMLInputElement);
  }
  if (root.nodeType === Node.TEXT_NODE) return convertText(root as Text);
  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;
  if (root === document.body) {
    const app = document.getElementById("root");
    if (app) convertTree(app);
    document.querySelectorAll("[data-portal], .mantine-Notifications-root").forEach((p) => convertTree(p));
    return;
  }
  if (root.nodeType === Node.ELEMENT_NODE) {
    if (skip(root as Element)) {
      // portal se dodaje u <body>: provjeri njegove potomke koji pripadaju aplikaciji
      (root as Element).querySelectorAll?.("[data-portal], .mantine-Notifications-root").forEach((p) => convertTree(p));
      return;
    }
    convertAttrs(root as Element);
  }
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  for (let n = w.nextNode(); n; n = w.nextNode()) {
    if (n.nodeType === Node.TEXT_NODE) convertText(n as Text);
    else {
      convertAttrs(n as Element);
      if ((n as Element).tagName === "INPUT") convertInput(n as HTMLInputElement);
    }
  }
}

/** Vraća izvorni (latinični) tekst svuda gdje ga je prevela ova komponenta, a React ga u međuvremenu nije promijenio. */
function restoreAll() {
  for (const [n, r] of touched) if (n.isConnected && n.nodeValue === r.conv) n.nodeValue = r.orig;
  for (const [el, rec] of touchedAttr) {
    if (!el.isConnected) continue;
    for (const [a, r] of Object.entries(rec)) if (el.getAttribute(a) === r.conv) el.setAttribute(a, r.orig);
  }
  for (const [el, r] of touchedInput) if (el.isConnected && el.value === r.conv) el.value = r.orig;
  touched.clear();
  touchedAttr.clear();
  touchedInput.clear();
}

function prune() {
  for (const n of touched.keys()) if (!n.isConnected) touched.delete(n);
  for (const el of touchedAttr.keys()) if (!el.isConnected) touchedAttr.delete(el);
  for (const el of touchedInput.keys()) if (!el.isConnected) touchedInput.delete(el);
}

function useCyrillicDom(on: boolean, version: number) {
  useEffect(() => {
    if (!on || typeof MutationObserver === "undefined") return;
    convertTree(document.body);
    const obs = new MutationObserver((muts) => {
      // povratni poziv ide prije iscrtavanja, pa se latinica ne vidi ni na trenutak
      for (const m of muts) {
        if (m.type === "childList") m.addedNodes.forEach((n) => n.isConnected && convertTree(n));
        else if (m.target.isConnected) convertTree(m.target);
      }
      if (touched.size > 5000) prune();
    });
    obs.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: [...ATTRS, "value", "readonly", "disabled"] });
    // React mijenja vrijednost polja bez događaja u DOM-u — povremena provjera polja koja se ne kucaju
    const iv = window.setInterval(() => document.querySelectorAll("input[readonly], input:disabled, input.mantine-Select-input")
      .forEach((el) => convertInput(el as HTMLInputElement)), 700);
    return () => { obs.disconnect(); window.clearInterval(iv); };
  }, [on, version]);
  useEffect(() => {
    if (!on) restoreAll();
  }, [on]);
}

// ------------------------------------------------------------------ kontekst
type Script = "lat" | "cyr";
interface Ctx {
  script: Script;
  setScript: (s: Script) => void;
  t: (s: string) => string;
  /** Ponovo učitaj listu izraza (poziva je administracija nakon izmjene). */
  reloadTerms: () => Promise<void>;
  terms: ScriptTerm[];
}
const ScriptCtx = createContext<Ctx>({ script: "lat", setScript: () => {}, t: (s) => s, reloadTerms: async () => {}, terms: [] });

export function ScriptProvider({ children }: { children: ReactNode }) {
  const [script, setScriptState] = useState<Script>(() => {
    try {
      return (localStorage.getItem("agrisense.script") as Script) || "lat";
    } catch {
      return "lat";
    }
  });
  const [terms, setTerms] = useState<ScriptTerm[]>(FALLBACK_TERMS);
  const [version, setVersion] = useState(0);

  const reloadTerms = useCallback(async () => {
    try {
      const r = await fetch("/api/v1/script-terms", { headers: { Accept: "application/json" } });
      if (!r.ok) return;
      const list = (await r.json()) as ScriptTerm[];
      if (Array.isArray(list)) {
        compile(list);
        setTerms(list);
        setVersion((v) => v + 1);
      }
    } catch {
      /* ostaje rezervna lista */
    }
  }, []);
  useEffect(() => {
    void reloadTerms();
  }, [reloadTerms]);

  // nova lista: vrati izvorni tekst pa ponovo prevedi sa novim pravilima
  useEffect(() => {
    if (version > 0 && script === "cyr") restoreAll();
  }, [version, script]);
  useCyrillicDom(script === "cyr", version);
  useEffect(() => {
    document.documentElement.lang = script === "cyr" ? "sr-Cyrl" : "sr-Latn";
  }, [script]);

  const value = useMemo<Ctx>(
    () => ({
      script,
      terms,
      reloadTerms,
      setScript: (s: Script) => {
        setScriptState(s);
        try {
          localStorage.setItem("agrisense.script", s);
        } catch {
          /* bez trajnog čuvanja */
        }
      },
      t: (s: string) => (script === "cyr" ? toCyrillic(s) : s),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [script, version, terms, reloadTerms],
  );
  return <ScriptCtx.Provider value={value}>{children}</ScriptCtx.Provider>;
}

export const useT = () => useContext(ScriptCtx);
